
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ArchiveFile, SearchResult, AIOverview } from '../types';
import { CLOUD_REGION, BUCKET_BASE_URL } from '../constants';
import { getResizedImageUrl } from '../services/storageService';

interface ArchiveGridProps {
  files: ArchiveFile[];
  searchResults: SearchResult[] | null;
  aiOverview: AIOverview | null;
  loadingAIOverview?: boolean;
  onFileSelect: (file: ArchiveFile) => void;
  onNarrowSearch?: (query: string) => void;
  title: string;
  bookmarks?: any[];
}

export const ArchiveGrid: React.FC<ArchiveGridProps> = ({ 
  files, 
  searchResults, 
  aiOverview, 
  loadingAIOverview, 
  onFileSelect, 
  onNarrowSearch,
  title,
  bookmarks 
}) => {
  const [altAnswer, setAltAnswer] = useState('');
  
  const isBookmarked = (fileId: string) => {
      return bookmarks?.some(b => b.fileId === fileId && !b.paragraphId);
  };
  const chatEndRef = useRef<HTMLDivElement>(null);
  const gridTopRef = useRef<HTMLDivElement>(null);
  
  // TTS State for Chat messages
  const [speakingIndex, setSpeakingIndex] = useState<number | null>(null);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);

  // Infinite Scroll State
  const ITEMS_PER_PAGE_P1 = 9; // Special layout for first 9 items
  const ITEMS_PER_PAGE_OTHER = 12; // How many to load on "Load More"
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE_P1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  // Generate a stable signature for the file list to prevent unnecessary scroll resets
  // The signature changes only when the category changes (likely changing first item) or filter changes (changing length)
  // This prevents parent re-renders from triggering the scroll-to-top effect when the file array reference changes but content is same.
  const filesSignature = useMemo(() => {
      if (files.length === 0) return 'empty';
      // Use length and first ID as a checksum. If user changes category or search, these likely change.
      return `${files.length}-${files[0].id}`;
  }, [files]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiOverview?.history, loadingAIOverview]);

  useEffect(() => {
    // Load voices
    const updateVoices = () => {
        setVoices(window.speechSynthesis.getVoices());
    };
    updateVoices();
    window.speechSynthesis.onvoiceschanged = updateVoices;

    return () => {
        window.speechSynthesis.cancel();
        window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  // Reset visible count when file CONTENT changes (e.g. category change, search)
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE_P1);
    gridTopRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [filesSignature]);

  const hasMore = visibleCount < files.length;

  const handleLoadMore = () => {
    if (!hasMore || isLoadingMore) return;
    
    setIsLoadingMore(true);
    // Simulate a small delay for smoother UI, then load
    setTimeout(() => {
        setVisibleCount(prev => prev + ITEMS_PER_PAGE_OTHER);
        setIsLoadingMore(false);
    }, 300);
  };

  // Intersection Observer for Infinite Scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
        (entries) => {
            const [entry] = entries;
            if (entry.isIntersecting && hasMore && !isLoadingMore) {
                handleLoadMore();
            }
        },
        {
            root: gridTopRef.current,
            rootMargin: '0px 0px 500px 0px', // Trigger when 500px from bottom
        }
    );

    const triggerElement = loadMoreTriggerRef.current;
    if (triggerElement) {
        observer.observe(triggerElement);
    }

    return () => {
        if (triggerElement) {
            observer.unobserve(triggerElement);
        }
    };
  }, [hasMore, isLoadingMore]);

  const handleAltSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (altAnswer.trim() && onNarrowSearch) {
      onNarrowSearch(altAnswer);
      setAltAnswer('');
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const normalizeTextForSpeech = (text: string): string => {
      if (!text) return '';
      // 1. Fix reading numbers: 0065-0003 -> 65-3
      let normalized = text.replace(/\b0+(\d+)-0+(\d+)\b/g, '$1-$2');
      normalized = normalized.replace(/\b0+([1-9]\d*)\b/g, '$1'); // 007 -> 7
      
      // 2. Fix all-caps words (2+ chars) to lowercase to prevent spelling out (e.g. "IN", "THE")
      normalized = normalized.replace(/\b([A-Z]{2,})\b/g, (match) => {
          return match.toLowerCase();
      });
      return normalized;
  };

  const handleSpeak = (text: string, index: number) => {
    if (speakingIndex === index) {
      window.speechSynthesis.cancel();
      setSpeakingIndex(null);
    } else {
      window.speechSynthesis.cancel();
      
      const el = document.getElementById(`archive-msg-${index}`);
      let textToSpeak = text;
      if (el && el.innerText) {
          textToSpeak = el.innerText;
      }
      
      const cleanText = normalizeTextForSpeech(textToSpeak.replace(/\[.*?\]/g, '').trim());
      const utterance = new SpeechSynthesisUtterance(cleanText);
      
      // Auto-select preferred voice
      let currentLang = document.documentElement.lang || 'en';
      const match = document.cookie.match(/googtrans=\/auto\/([a-zA-Z-]+)/);
      if (match && match[1]) {
          currentLang = match[1];
      }
      utterance.lang = currentLang;
      const matchingVoices = voices.filter(v => v.lang.startsWith(currentLang.split('-')[0]));
      
      // Sort: Microsoft > Google, Natural/Online > Standard
      matchingVoices.sort((a, b) => {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();
          const scoreA = (aName.includes('microsoft') ? 2 : 0) + (aName.includes('natural') ? 1 : 0) + (aName.includes('online') ? 0.5 : 0);
          const scoreB = (bName.includes('microsoft') ? 2 : 0) + (bName.includes('natural') ? 1 : 0) + (bName.includes('online') ? 0.5 : 0);
          return scoreB - scoreA;
      });
      
      if (matchingVoices.length > 0) {
          utterance.voice = matchingVoices[0];
      }

      utterance.onend = () => setSpeakingIndex(null);
      window.speechSynthesis.speak(utterance);
      setSpeakingIndex(index);
    }
  };

  const renderSynthesis = (text: string) => {
    return (
      <div className="space-y-4 font-sans text-[0.9375rem] leading-relaxed">
        {text.split('\n').map((line, i) => {
          if (line.includes('[DATA SYNTHESIS]') || line.includes('[DOCUMENTED PROTOCOL]')) {
             return <h5 key={i} className="text-amber-800 dark:text-amber-500 font-bold uppercase tracking-widest text-xs mt-4">{line.replace(/[\[\]]/g, '')}</h5>;
          }
          return line.trim() ? <p key={i} className="text-stone-800 dark:text-stone-300">{line}</p> : null;
        })}
      </div>
    );
  };

  const showWikiView = !searchResults && !aiOverview && !loadingAIOverview;

  // Slicing logic for magazine layout and infinite grid
  const visibleFiles = files.slice(0, visibleCount);
  const heroFile = visibleFiles.length > 0 ? visibleFiles[0] : null;
  const grid3Files = visibleFiles.slice(1, 4);
  const bannerFile = visibleFiles.length > 4 ? visibleFiles[4] : null;
  // All remaining files after the initial magazine layout parts
  const extendedGridFiles = visibleFiles.slice(5);

  const renderWikiView = () => (
    <div className="space-y-6">
        <div className="space-y-6">
            {/* 1. HERO SECTION */}
            {heroFile && (
                <div onClick={() => onFileSelect(heroFile)} className={`cursor-pointer group relative h-80 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 ${isBookmarked(heroFile.id) ? 'ring-2 ring-amber-500 ring-offset-4 dark:ring-offset-stone-900 border-none' : ''}`}>
                    <img src={getResizedImageUrl(heroFile.imageUrl, '1800x1800')} alt={heroFile.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-90"></div>
                    <div className="absolute top-4 right-4 z-10 transition-transform group-hover:scale-110">
                        {isBookmarked(heroFile.id) && (
                            <div className="bg-amber-500 text-white p-2 rounded-full shadow-lg">
                                <i className="fa-solid fa-bookmark"></i>
                            </div>
                        )}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-8 text-center">
                        <div className="inline-block px-3 py-1 mb-3 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-[0.625rem] font-bold text-white uppercase tracking-widest">Featured Record</div>
                        <h2 className="text-3xl md:text-5xl font-serif text-white mb-3 drop-shadow-lg group-hover:text-amber-200 transition-colors uppercase tracking-tight">{heroFile.name}</h2>
                        <p className="text-stone-200 text-sm md:text-base max-w-2xl mx-auto line-clamp-2 font-light tracking-wide drop-shadow-md">{heroFile.description}</p>
                    </div>
                </div>
            )}

            {/* 2. GRID OF 3 */}
            {grid3Files.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {grid3Files.map((file) => (
                        <div key={file.id} onClick={() => onFileSelect(file)} className={`cursor-pointer bg-[#fdfbf7] dark:bg-stone-800/80 backdrop-blur-md rounded-xl overflow-hidden shadow-md hover:shadow-xl hover:-translate-y-1 transition-all border group relative ${isBookmarked(file.id) ? 'border-amber-500 ring-1 ring-amber-500/20' : 'border-stone-200/50 dark:border-white/10'}`}>
                            {isBookmarked(file.id) && (
                                <div className="absolute top-3 right-3 z-10 text-amber-500 bg-amber-50 dark:bg-amber-900/40 w-6 h-6 flex items-center justify-center rounded-full shadow-sm animate-in zoom-in duration-300">
                                    <i className="fa-solid fa-bookmark text-[10px]"></i>
                                </div>
                            )}
                            <div className="h-40 overflow-hidden relative">
                                <img src={getResizedImageUrl(file.imageUrl, '300x300')} alt={file.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                            </div>
                            <div className="p-5 text-center">
                                <h3 className="text-lg font-serif text-stone-800 dark:text-stone-100 mb-2 group-hover:text-amber-700 dark:group-hover:text-amber-400 transition-colors">{file.name}</h3>
                                <p className="text-[0.6875rem] text-stone-500 dark:text-stone-400 leading-relaxed line-clamp-3">{file.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* 3. WIDE BANNER */}
            {bannerFile && (
                <div onClick={() => onFileSelect(bannerFile)} className={`cursor-pointer relative h-64 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group border ${isBookmarked(bannerFile.id) ? 'border-amber-500 ring-2 ring-amber-500/10' : 'border-stone-200 dark:border-stone-700'}`}>
                    <img src={getResizedImageUrl(bannerFile.imageUrl, '1800x1800')} alt={bannerFile.name} className="absolute inset-0 w-full h-full object-cover" />
                    {isBookmarked(bannerFile.id) && (
                        <div className="absolute top-4 right-4 z-10 text-amber-500 bg-white/90 dark:bg-stone-900/90 w-8 h-8 flex items-center justify-center rounded-full shadow-md">
                            <i className="fa-solid fa-bookmark"></i>
                        </div>
                    )}
                    <div className="absolute inset-0 bg-[#fdfbf7]/90 dark:bg-stone-900/90 backdrop-blur-sm m-3 md:m-6 rounded-xl border border-stone-200 dark:border-white/10 flex flex-col items-center justify-center p-8 text-center transition-colors">
                        <span className="text-amber-600 dark:text-amber-500 text-xs font-bold uppercase tracking-widest mb-2">Editor's Pick</span>
                        <h2 className="text-2xl md:text-3xl font-serif text-stone-900 dark:text-white mb-3 group-hover:text-amber-700 dark:group-hover:text-amber-400 transition-colors">{bannerFile.name}</h2>
                        <p className="text-stone-600 dark:text-stone-300 text-sm max-w-xl mx-auto leading-relaxed">{bannerFile.description}</p>
                        <div className="mt-6 flex items-center gap-2 text-xs font-bold text-stone-400 dark:text-stone-500 group-hover:text-amber-600 dark:group-hover:text-amber-500 transition-colors">
                            <span>Read Article</span>
                            <i className="fa-solid fa-arrow-right"></i>
                        </div>
                    </div>
                </div>
            )}

            {/* 4. EXTENDED/STANDARD GRID */}
            {extendedGridFiles.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {extendedGridFiles.map((file) => (
                        <div key={file.id} onClick={() => onFileSelect(file)} className={`cursor-pointer bg-[#fdfbf7] dark:bg-stone-800/80 backdrop-blur-md rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all border group flex flex-col relative ${isBookmarked(file.id) ? 'border-amber-500 ring-1 ring-amber-500/20' : 'border-stone-200/50 dark:border-white/10'}`}>
                            {isBookmarked(file.id) && (
                                <div className="absolute top-3 right-3 z-10 text-amber-500 bg-white/90 dark:bg-stone-900/90 w-6 h-6 flex items-center justify-center rounded-full shadow-sm">
                                    <i className="fa-solid fa-bookmark text-[10px]"></i>
                                </div>
                            )}
                            <div className="h-48 overflow-hidden relative shrink-0">
                                <img src={getResizedImageUrl(file.imageUrl, '300x300')} alt={file.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60"></div>
                            </div>
                            <div className="p-5 flex-1 flex flex-col justify-between">
                                <div>
                                    <h3 className="text-lg font-serif text-stone-800 dark:text-stone-100 mb-2 group-hover:text-amber-700 dark:group-hover:text-amber-400 transition-colors">{file.name}</h3>
                                    <p className="text-[0.6875rem] text-stone-500 dark:text-stone-400 leading-relaxed line-clamp-3 mb-4">{file.description}</p>
                                </div>
                                <div className="pt-4 border-t border-stone-100 dark:border-stone-700 flex items-center justify-between">
                                    <span className="text-[0.5625rem] font-bold text-stone-400 uppercase tracking-widest">{file.category}</span>
                                    <i className="fa-solid fa-arrow-right-long text-amber-500 opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0"></i>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
        
        {/* LOAD MORE TRIGGER AND INDICATOR */}
        <div ref={loadMoreTriggerRef} className="h-1" />
        {isLoadingMore && (
            <div className="flex justify-center mt-8 mb-8">
                <i className="fa-solid fa-circle-notch animate-spin text-2xl text-amber-500"></i>
            </div>
        )}
    </div>
  );

  const renderSplitView = () => (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-4">
        <div className="lg:col-span-8 space-y-6">
            <div className="bg-[#fdfbf7] dark:bg-stone-800 rounded-[2rem] border border-stone-200 dark:border-stone-700 shadow-xl overflow-hidden flex flex-col h-[650px] transition-colors">
                <div className="flex-1 overflow-y-auto p-8 space-y-6 bg-[#e6dec6]/40 dark:bg-stone-900/40 custom-scrollbar">
                    {aiOverview?.history.map((msg, i) => (
                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in`}>
                            <div id={`archive-msg-${i}`} className={`max-w-[95%] p-6 rounded-2xl relative group transition-colors ${msg.role === 'user' ? 'bg-stone-800 dark:bg-stone-700 text-white rounded-tr-none shadow-md' : 'bg-white dark:bg-stone-800 border border-stone-100 dark:border-stone-700 text-stone-800 dark:text-stone-200 rounded-tl-none shadow-sm'}`}>
                                {msg.role === 'ai' ? renderSynthesis(msg.text) : <p className="text-sm">{msg.text}</p>}
                                {msg.role === 'ai' && (
                                    <div className="absolute bottom-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 dark:bg-stone-700/90 backdrop-blur-sm p-1 rounded-lg shadow-sm border border-stone-100 dark:border-stone-600 z-10">
                                        <button onClick={() => handleSpeak(msg.text, i)} className={`w-7 h-7 flex items-center justify-center rounded-md hover:bg-stone-100 dark:hover:bg-stone-600 transition-colors ${speakingIndex === i ? 'text-amber-600 bg-amber-50 dark:bg-amber-900/30' : 'text-stone-500 dark:text-stone-300 hover:text-amber-600'}`} title={speakingIndex === i ? "Stop" : "Read Aloud"}><i className={`fa-solid ${speakingIndex === i ? 'fa-stop' : 'fa-volume-high'} text-xs`}></i></button>
                                        <div className="w-px h-4 bg-stone-200 dark:bg-stone-600 mx-0.5"></div>
                                        <button onClick={() => handleCopy(msg.text)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-stone-100 dark:hover:bg-stone-600 text-stone-500 dark:text-stone-300 hover:text-stone-800 dark:hover:text-white transition-colors" title="Copy to Clipboard"><i className="fa-regular fa-copy text-xs"></i></button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    {loadingAIOverview && (
                        <div className="flex justify-start">
                            <div className="bg-white dark:bg-stone-800 border border-stone-100 dark:border-stone-700 p-4 rounded-2xl rounded-tl-none shadow-sm flex items-center space-x-2">
                                <span className="text-[0.5625rem] font-bold text-stone-400 uppercase tracking-widest">Synthesizing Archive Streams...</span>
                                <i className="fa-solid fa-circle-notch animate-spin text-amber-500"></i>
                            </div>
                        </div>
                    )}
                    {aiOverview && !loadingAIOverview && (
                        <div className="mt-8 pt-6 border-t border-stone-100 dark:border-stone-700">
                            <p className="text-[0.625rem] font-bold text-stone-900 dark:text-stone-300 mb-4 uppercase tracking-[0.2em] flex items-center"><i className="fa-solid fa-magnifying-glass-chart mr-2 text-amber-600"></i>{aiOverview.narrowingQuestion}</p>
                            <div className="flex flex-wrap gap-2">
                                {aiOverview.options.map((option, i) => (
                                    <button key={i} onClick={() => onNarrowSearch && onNarrowSearch(option)} className="px-4 py-2.5 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-600 rounded-xl text-xs text-stone-700 dark:text-stone-300 hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/30 hover:text-amber-900 dark:hover:text-amber-100 transition-all font-semibold shadow-sm">{option}</button>
                                ))}
                            </div>
                        </div>
                    )}
                    <div ref={chatEndRef} />
                </div>
                <div className="p-4 bg-[#fdfbf7] dark:bg-stone-800 border-t border-stone-100 dark:border-stone-700">
                    <form onSubmit={handleAltSubmit} className="relative">
                        <input type="text" placeholder="Input investigation pattern or health inquiry..." value={altAnswer} onChange={(e) => setAltAnswer(e.target.value)} className="w-full pl-5 pr-14 py-4 bg-stone-100 dark:bg-stone-900 border-none rounded-xl text-sm focus:ring-2 focus:ring-amber-50 dark:focus:ring-amber-900 outline-none transition-all placeholder:text-stone-400 dark:placeholder:text-stone-600 text-stone-800 dark:text-stone-200" />
                        <button type="submit" disabled={loadingAIOverview} className="absolute right-2 top-2 p-2.5 bg-stone-900 dark:bg-stone-700 text-white rounded-lg hover:bg-stone-700 dark:hover:bg-stone-600 transition-all disabled:opacity-30"><i className="fa-solid fa-paper-plane text-xs"></i></button>
                    </form>
                </div>
            </div>
        </div>
        <div className="lg:col-span-4 space-y-4">
            <div className="bg-[#fdfbf7]/90 dark:bg-stone-800/90 backdrop-blur rounded-2xl p-4 border border-stone-200/50 dark:border-stone-700 shadow-xl max-h-[650px] overflow-y-auto">
                <h3 className="text-xs font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-4">Search Hits</h3>
                <div className="space-y-3">
                    {searchResults?.map((result, idx) => (
                        <div key={idx} onClick={() => onFileSelect(result.file)} className="p-3 rounded-xl hover:bg-amber-50 dark:hover:bg-amber-900/20 cursor-pointer transition-colors border-b border-stone-100 dark:border-stone-700 last:border-0">
                            <h4 className="font-serif font-bold text-stone-800 dark:text-stone-200 text-sm mb-1">{result.file.name}</h4>
                            <p className="text-[0.625rem] text-stone-500 dark:text-stone-400 line-clamp-2">{result.snippet}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </div>
  );

  const renderSearchResults = () => (
    <div className="max-w-4xl mx-auto space-y-4 pb-20">
        <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] text-stone-400 font-bold uppercase tracking-[0.2em]">{searchResults?.length || 0} matches found</p>
        </div>
        {searchResults?.length && searchResults.length > 0 ? searchResults.map((res, i) => (
            <div key={res.file.id} onClick={() => onFileSelect(res.file)} className="flex gap-4 p-4 bg-white dark:bg-stone-800 rounded-2xl border border-stone-200 dark:border-stone-700 shadow-sm cursor-pointer hover:border-amber-500 hover:shadow-md transition-all group animate-in fade-in slide-in-from-bottom-2 duration-300" style={{ animationDelay: `${Math.min(i * 50, 500)}ms` }}>
                
                {/* Article Banner Image Thumbnail */}
                {res.file.imageUrl && (
                    <div className="w-24 h-24 sm:w-32 sm:h-32 shrink-0 rounded-xl overflow-hidden bg-stone-100 dark:bg-stone-900 border border-stone-100 dark:border-stone-700/50">
                        <img src={getResizedImageUrl(res.file.imageUrl, '300x300')} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    </div>
                )}

                <div className="flex-1 min-w-0 flex flex-col">
                    <div className="flex justify-between items-start mb-1">
                        <h3 className="font-serif font-bold text-lg sm:text-xl text-stone-800 dark:text-stone-100 group-hover:text-amber-600 transition-colors truncate pr-4">{res.file.name}</h3>
                        <span className="shrink-0 px-2 py-0.5 bg-stone-100 dark:bg-stone-700 text-[9px] font-bold text-stone-500 uppercase rounded">{res.file.category}</span>
                    </div>
                    
                    <p className="text-[10px] text-stone-400 mb-2 flex items-center gap-1 font-mono truncate">
                        <i className="fa-solid fa-link text-[8px]"></i>
                        {res.file.path}
                    </p>
                    
                    <p className="text-xs sm:text-sm text-stone-600 dark:text-stone-400 leading-relaxed line-clamp-2 sm:line-clamp-3">
                        {res.snippet}
                    </p>
                </div>
            </div>
        )) : (
            <div className="py-20 text-center space-y-4">
                <i className="fa-solid fa-magnifying-glass-blur text-4xl text-stone-200 dark:text-stone-700"></i>
                <p className="text-stone-500 dark:text-stone-400">No matches found in currently indexed records.</p>
            </div>
        )}
    </div>
  );

  return (
    <div className="h-full w-full overflow-y-auto overflow-x-hidden custom-scrollbar relative" ref={gridTopRef}>
      {showWikiView && (
        <div className="fixed inset-0 z-0 pointer-events-none">
           <div className="absolute inset-0 bg-stone-900 hidden dark:block">
             <img 
               src="https://images.unsplash.com/photo-1519681393784-d120267933ba?q=80&w=2400&auto=format&fit=crop" 
               className="w-full h-full object-cover opacity-60 mix-blend-overlay"
               alt="background"
             />
             <div className="absolute inset-0 bg-gradient-to-b from-transparent via-stone-900/20 to-stone-900/80"></div>
           </div>
           <div className="absolute inset-0 bg-[#e6dec6] dark:hidden">
             <div className="absolute inset-0 bg-gradient-to-b from-[#f2efe4]/50 via-[#e6dec6] to-[#f2efe4]/50"></div>
             <div className="absolute inset-0 opacity-[0.03] bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')]"></div>
           </div>
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8 animate-in fade-in duration-700">
        {showWikiView ? renderWikiView() : (
            (aiOverview || loadingAIOverview) ? renderSplitView() : renderSearchResults()
        )}
      </div>
    </div>
  );
};
